import type { DeepMaybeRef } from "@vueuse/core";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import type { ScenarioCalculations } from "../calculationTypes";
import { FORWARD_REF_NAMES, type ScenarioCalcContext } from "./context";
import { createScenarioCalcInputs } from "./inputs";
import { defineEquipmentOrdering } from "./equipmentOrdering";
import { defineLengthsAndDepths } from "./lengthsAndDepths";
import { defineDiametersAndWeights } from "./diametersAndWeights";
import { defineWellInputs } from "./wellInputs";
import { defineFluidProperties } from "./fluidProperties";
import { defineIntakeConditions } from "./intakeConditions";
import { definePumpHydraulics } from "./pumpHydraulics";
import { definePumpLimits } from "./pumpLimits";
import { defineFriction } from "./friction";
import { defineMotor } from "./motor";
import { defineSeals } from "./seals";
import { defineCables } from "./cables";
import { defineSurfaceDrive } from "./surfaceDrive";
import { buildScenarioCalculations } from "./buildScenarioCalculations";

export const useScenarioCalculations = (
  destructuredScenario: DeepMaybeRef<DestructuredScenario>,
  overrides?: Partial<ScenarioCalculations>
  // extraParameters?: object
): ScenarioCalculations => {
  // Starts as just the inputs; each domain below adds its calcs. See
  // ForwardRefs in ./context for how domains read calcs defined after them.
  // Every read of ctx happens inside a computed getter or after this setup,
  // so the cast is safe.
  const ctx = createScenarioCalcInputs(
    destructuredScenario,
    overrides
  ) as ScenarioCalcContext;

  Object.assign(ctx, defineEquipmentOrdering(ctx));
  Object.assign(ctx, defineLengthsAndDepths(ctx));
  Object.assign(ctx, defineDiametersAndWeights(ctx));
  Object.assign(ctx, defineWellInputs(ctx));
  Object.assign(ctx, defineFluidProperties(ctx));
  Object.assign(ctx, defineIntakeConditions(ctx));
  Object.assign(ctx, definePumpHydraulics(ctx));
  Object.assign(ctx, definePumpLimits(ctx));
  Object.assign(ctx, defineFriction(ctx));
  Object.assign(ctx, defineMotor(ctx));
  Object.assign(ctx, defineSeals(ctx));
  Object.assign(ctx, defineCables(ctx));
  Object.assign(ctx, defineSurfaceDrive(ctx));

  // Domains read these from ctx inside computed getters, so a missing one
  // would only surface later as a confusing "undefined" error. The types
  // already guarantee they exist; this catches anything that bypasses them.
  for (const name of FORWARD_REF_NAMES) {
    if (ctx[name] === undefined) {
      throw new Error(
        `useScenarioCalculations: "${name}" is read by an earlier domain but no domain defined it`
      );
    }
  }

  return buildScenarioCalculations(ctx);
};
